import React, { useEffect, useState, useRef } from 'react'
import { useWheelStore } from '../stores/wheelStore'
import { Wheel } from '../components/Wheel'
import { OptionList } from '../components/OptionList'
import { ContextInputModal } from '../components/ContextInputModal'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

const ENGLISH_NAMES = [
  'James', 'Mary', 'John', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda',
  'William', 'Elizabeth', 'David', 'Barbara', 'Richard', 'Susan', 'Joseph', 'Jessica',
  'Thomas', 'Sarah', 'Charles', 'Karen', 'Christopher', 'Nancy', 'Daniel', 'Lisa',
  'Matthew', 'Betty', 'Anthony', 'Margaret', 'Mark', 'Sandra'
]

const ResultModal: React.FC<{
  open: boolean
  onClose: () => void
  children: React.ReactNode
}> = ({ open, onClose, children }) => {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-2xl p-8 min-w-[320px] max-w-xl relative">
        <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-2xl" onClick={onClose}>×</button>
        {children}
      </div>
    </div>
  )
}

const TeamPickerPage: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const {
    options,
    isSpinning,
    result,
    addOption,
    removeOption,
    updateOption,
    spin,
    spinEnd,
    mode,
    setMode,
    pendingElimination,
    accumulationCounts,
    soundEnabled
  } = useWheelStore()
  const [initialized, setInitialized] = useState(false)
  const [isContextModalOpen, setIsContextModalOpen] = useState(false)
  const [showResultModal, setShowResultModal] = useState(false)
  const prevSpinning = useRef(isSpinning)
  const navigate = useNavigate()

  useEffect(() => {
    if (!initialized) {
      useWheelStore.setState({
        options: ENGLISH_NAMES.map((name, i) => ({ id: `${Date.now()}_${i}`, text: name })),
        result: null,
        isSpinning: false,
        history: []
      })
      setInitialized(true)
    }
  }, [initialized])

  useEffect(() => {
    if (prevSpinning.current && !isSpinning && result) {
      setShowResultModal(true)
    }
    prevSpinning.current = isSpinning
  }, [isSpinning, result])

  const showResult = (mode !== 'accumulation' && result && !isSpinning) ||
    (mode === 'accumulation' && (() => {
      const maxCount = Math.max(0, ...Object.values(accumulationCounts))
      const mostPicked = options.filter(o => (accumulationCounts[o.id] || 0) === maxCount && maxCount > 0)
      return mostPicked.length > 0 && !isSpinning
    })())

  return (
    <>
      <Helmet>
        <title>Team Picker Wheel - AI Decision Maker for Team Selection | Free Online Tool</title>
        <meta name="description" content="Use our AI decision maker to randomly pick team members and create fair groups. Features spinning wheel and AI analysis for team selection. Free online tool." />
        <meta name="keywords" content="team picker wheel, team selection tool, ai decision maker team, random team picker, group selection tool, team building tool" />
        <meta property="og:title" content="Team Picker Wheel - AI Decision Maker" />
        <meta property="og:description" content="Randomly pick team members with our AI decision maker. Free online tool with AI analysis." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aidecision.org/tools/team-picker" />
        <meta name="twitter:title" content="Team Picker Wheel - AI Decision Maker" />
        <meta name="twitter:description" content="Randomly pick team members with AI analysis." />
        <link rel="canonical" href="https://aidecision.org/tools/team-picker" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Team Picker Wheel - AI Decision Maker",
            "description": "Randomly pick team members with AI-powered analysis",
            "url": "https://aidecision.org/tools/team-picker",
            "applicationCategory": "ProductivityApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Team member random selection",
              "AI-powered decision analysis",
              "Multiple decision modes",
              "Fair group creation",
              "Free online tool"
            ],
            "author": {
              "@type": "Organization",
              "name": "AI Decision Maker"
            }
          })}
        </script>
      </Helmet>

      <div className="max-w-4xl mx-auto">
        <header className="text-center mb-6">
          <h1 className="text-2xl font-bold mb-4">Team Picker Wheel - AI Decision Maker</h1>
          <p className="text-gray-600">Randomly select team members with our AI decision maker. Get instant AI analysis of your team composition.</p>
        </header>
        <div className="flex justify-center mb-6 gap-4">
          <button className={`px-6 py-2 rounded-t-lg font-bold border-b-4 transition-colors ${mode === 'normal' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-transparent bg-gray-100 text-gray-500'}`} onClick={() => setMode('normal')}>Normal</button>
          <button className={`px-6 py-2 rounded-t-lg font-bold border-b-4 transition-colors ${mode === 'elimination' ? 'border-green-600 bg-green-50 text-green-700' : 'border-transparent bg-gray-100 text-gray-500'}`} onClick={() => setMode('elimination')}>Elimination</button>
          <button className={`px-6 py-2 rounded-t-lg font-bold border-b-4 transition-colors ${mode === 'accumulation' ? 'border-purple-600 bg-purple-50 text-purple-700' : 'border-transparent bg-gray-100 text-gray-500'}`} onClick={() => setMode('accumulation')}>Accumulation</button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <OptionList options={options} onAddOption={addOption} onRemoveOption={removeOption} onUpdateOption={updateOption} />
          </div>
          <div className="flex flex-col items-center">
            <Wheel options={options} isSpinning={isSpinning} result={result} onSpin={spin} onSpinEnd={spinEnd} mode={mode} pendingElimination={pendingElimination} soundEnabled={soundEnabled} />
          </div>
        </div>
        <ResultModal open={showResultModal && showResult} onClose={() => setShowResultModal(false)}>
          <div className="relative z-10 w-full text-center">
            <h3 className="text-lg font-medium text-gray-700 mb-2 flex items-center justify-center gap-2">
              <span role="img" aria-label="target">🎯</span> Decision Result
            </h3>
            {mode === 'accumulation' ? (
              (() => {
                const total = Object.values(accumulationCounts).reduce((a, b) => a + b, 0)
                const maxCount = Math.max(0, ...Object.values(accumulationCounts))
                const mostPicked = options.filter(o => (accumulationCounts[o.id] || 0) === maxCount && maxCount > 0)
                return mostPicked.length > 0 ? (
                  <>
                    <div className="text-3xl font-extrabold text-primary-600 mb-2">
                      {mostPicked.map(o => o.text).join(', ')}
                    </div>
                    <div className="text-base text-gray-700 mb-2">
                      Based on accumulation, the most picked option{mostPicked.length > 1 ? 's are' : ' is'} above ({maxCount} times, {total > 0 ? ((maxCount / total) * 100).toFixed(1) : '0.0'}%).
                    </div>
                  </>
                ) : null
              })()
            ) : mode === 'elimination' ? (
              options.length === 1 ? (
                <>
                  <div className="text-3xl font-extrabold text-primary-600 mb-2">{options[0].text}</div>
                  <div className="text-base text-gray-700 mb-2">Only one option left after elimination.</div>
                </>
              ) : (
                <>
                  <div className="text-lg font-semibold text-primary-700 mb-2">Remaining Options</div>
                  <div className="flex flex-wrap justify-center gap-2 mb-2">
                    {options.length > 0 ? options.map(o => (
                      <span key={o.id} className="inline-block bg-blue-100 text-blue-800 font-semibold px-4 py-2 rounded-full shadow-sm text-base">
                        {o.text}
                      </span>
                    )) : <span className="text-gray-400">No options left</span>}
                  </div>
                  <div className="text-base text-gray-700 mb-2">Below are the remaining options after elimination.</div>
                </>
              )
            ) : (
              <>
                <div className="text-3xl font-extrabold text-primary-600 mb-2">{result?.text}</div>
                <div className="text-base text-gray-700 mb-2">Click the button below to discuss this decision with AI</div>
              </>
            )}
          </div>
          <div className="w-full border-t border-gray-200 my-6"></div>
          <div className="w-full flex flex-col items-center">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">🤖</span>
              <span className="text-lg font-bold text-blue-700">AI Analysis</span>
            </div>
            <p className="text-blue-800 text-base font-medium mb-2">Want to know what your result means? Let AI instantly analyze your decision and give you smart advice!</p>
            <button
              onClick={() => setIsContextModalOpen(true)}
              className="btn-primary px-8 py-3 text-lg mt-2 shadow-lg hover:scale-105 transition-transform"
            >
              Get AI Analysis Now
            </button>
          </div>
        </ResultModal>
        <ContextInputModal
          isOpen={isContextModalOpen}
          onClose={() => setIsContextModalOpen(false)}
          onConfirm={(context) => {
            let aiContext = ''
            if (mode === 'normal') {
              aiContext = `Wheel result: ${result?.text}\n\n${context}`
            } else if (mode === 'elimination') {
              aiContext = `Elimination mode. Remaining options: ${options.map(o => o.text).join(', ')}.\n\n${context}`
            } else if (mode === 'accumulation') {
              aiContext = context
            }
            navigate('/chat', {
              state: {
                wheelResult: result,
                context: aiContext,
              },
            })
          }}
          result={result}
          mode={mode}
          options={options}
          accumulationCounts={accumulationCounts}
        />
      </div>
    </>
  )
}

export default TeamPickerPage 